import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  ACCENT_PARAM,
  STYLING_PARAM,
  THEME_PARAM,
  accentToParamValue,
  buildLandingStylingShareUrl,
  parseLandingStylingParams,
} from '../utils/landingStylingParams';

/**
 * Homepage styling preview via query params (?styling=true&accent=1fad8a&theme=light).
 * Syncs accent/theme changes back into the URL for shareable links.
 */
export default function useLandingStylingParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const { stylingAllowed, accent, theme } = useMemo(
    () => parseLandingStylingParams(searchParams),
    [searchParams],
  );

  const updateParams = useCallback(
    (mutate) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setAccentParam = useCallback(
    (hex) => {
      if (!stylingAllowed) return;
      const value = accentToParamValue(hex);
      updateParams((params) => {
        if (value) params.set(ACCENT_PARAM, value);
        else params.delete(ACCENT_PARAM);
      });
    },
    [stylingAllowed, updateParams],
  );

  const setThemeParam = useCallback(
    (themeName) => {
      if (!stylingAllowed) return;
      updateParams((params) => {
        if (themeName === 'light' || themeName === 'dark') params.set(THEME_PARAM, themeName);
        else params.delete(THEME_PARAM);
      });
    },
    [stylingAllowed, updateParams],
  );

  const getShareUrl = useCallback(
    ({ accent: accentHex, theme: themeName } = {}) => buildLandingStylingShareUrl({
      origin: window.location.origin,
      pathname: location.pathname === '/landing' ? '/home' : location.pathname,
      searchParams,
      accent: accentHex ?? accent,
      theme: themeName ?? theme,
      variant: searchParams.get('v') || 'a',
    }),
    [accent, theme, location.pathname, searchParams],
  );

  return {
    stylingAllowed,
    accentFromUrl: accent,
    themeFromUrl: theme,
    setAccentParam,
    setThemeParam,
    getShareUrl,
    searchParams,
  };
}
